export const AUTO_NAG_MIN_INTERVAL_HOURS = 12;
export const AUTO_NAG_MAX_INTERVAL_HOURS = 30 * 24;
export const AUTO_NAG_MIN_INTERVAL_DAYS = 1;
export const AUTO_NAG_MAX_INTERVAL_DAYS = 30;
export const AUTO_NAG_COOLDOWN_MS = 12 * 60 * 60 * 1000;

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value, keys) {
  if (!isPlainObject(value)) return false;
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => actual.includes(key));
}

function resolveDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? new Date(value) : null;
  if (typeof value.toDate === "function") {
    const date = value.toDate();
    return date instanceof Date && Number.isFinite(date.getTime()) ? date : null;
  }
  return null;
}

export function isValidTimeZone(timeZone) {
  const normalized = String(timeZone || "").trim();
  if (!normalized || normalized.length > 100) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: normalized }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}

export function normalizeAutoNagSettings(value) {
  if (value === undefined || value === null) {
    return { valid: true, reason: "missing", settings: null };
  }
  if (!hasExactKeys(value, ["enabled", "interval", "firstSendAt", "timeZone"])) {
    return { valid: false, reason: "invalid-root", settings: null };
  }
  if (value.enabled !== true && value.enabled !== false) {
    return { valid: false, reason: "invalid-enabled", settings: null };
  }
  if (!hasExactKeys(value.interval, ["value", "unit"])) {
    return { valid: false, reason: "invalid-interval", settings: null };
  }
  const intervalValue = value.interval.value;
  const intervalUnit = value.interval.unit;
  const intervalValid = Number.isInteger(intervalValue) && (
    (intervalUnit === "hours" &&
      intervalValue >= AUTO_NAG_MIN_INTERVAL_HOURS &&
      intervalValue <= AUTO_NAG_MAX_INTERVAL_HOURS) ||
    (intervalUnit === "days" &&
      intervalValue >= AUTO_NAG_MIN_INTERVAL_DAYS &&
      intervalValue <= AUTO_NAG_MAX_INTERVAL_DAYS)
  );
  if (!intervalValid) return { valid: false, reason: "invalid-interval", settings: null };
  const firstSendAt = resolveDate(value.firstSendAt);
  if (!firstSendAt) return { valid: false, reason: "invalid-first-send", settings: null };
  const timeZone = String(value.timeZone || "").trim();
  if (!isValidTimeZone(timeZone)) {
    return { valid: false, reason: "invalid-time-zone", settings: null };
  }
  return {
    valid: true,
    reason: null,
    settings: {
      enabled: value.enabled,
      interval: { value: intervalValue, unit: intervalUnit },
      firstSendAt,
      timeZone,
    },
  };
}

export function createDefaultAutoNagSettings({ now = new Date(), timeZone = "UTC" } = {}) {
  const resolvedTimeZone = isValidTimeZone(timeZone) ? timeZone : "UTC";
  const current = resolveDate(now) || new Date();
  const currentParts = zonedParts(current, resolvedTimeZone);
  const firstSendAt = localPartsToInstant(
    { ...addLocalDays(currentParts, 1), hour: 9, minute: 0, second: 0 },
    resolvedTimeZone
  );
  return {
    enabled: false,
    interval: { value: 1, unit: "days" },
    firstSendAt,
    timeZone: resolvedTimeZone,
  };
}

export function createAutoNagSettingsSnapshot(value, options = {}) {
  const normalized = normalizeAutoNagSettings(value);
  return normalized.valid && normalized.settings
    ? normalized.settings
    : createDefaultAutoNagSettings(options);
}

function zonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  };
}

function localSortValue(parts) {
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
}

function sameLocalParts(left, right) {
  return ["year", "month", "day", "hour", "minute", "second"]
    .every((key) => left[key] === right[key]);
}

function timeZoneOffsetMinutes(date, timeZone) {
  return Math.round((localSortValue(zonedParts(date, timeZone)) - date.getTime()) / 60000);
}

function localPartsToInstant(parts, timeZone) {
  const naiveUtc = localSortValue(parts);
  const offsets = new Set([-24, -12, 0, 12, 24].map((hours) =>
    timeZoneOffsetMinutes(new Date(naiveUtc + hours * 60 * 60 * 1000), timeZone)
  ));
  const exact = Array.from(offsets)
    .map((offset) => new Date(naiveUtc - offset * 60 * 1000))
    .filter((date) => sameLocalParts(zonedParts(date, timeZone), parts))
    .sort((left, right) => left.getTime() - right.getTime());
  if (exact.length) return exact[0];

  const targetLocal = localSortValue(parts);
  let best = null;
  let bestLocal = Number.POSITIVE_INFINITY;
  for (let minute = -14 * 60; minute <= 14 * 60; minute += 1) {
    const candidate = new Date(naiveUtc + minute * 60 * 1000);
    const candidateParts = zonedParts(candidate, timeZone);
    if (
      candidateParts.year !== parts.year ||
      candidateParts.month !== parts.month ||
      candidateParts.day !== parts.day
    ) continue;
    const candidateLocal = localSortValue(candidateParts);
    if (candidateLocal >= targetLocal && candidateLocal < bestLocal) {
      best = candidate;
      bestLocal = candidateLocal;
    }
  }
  return best;
}

function addLocalDays(parts, days) {
  const date = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day + days,
    parts.hour,
    parts.minute,
    parts.second
  ));
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: date.getUTCHours(),
    minute: date.getUTCMinutes(),
    second: date.getUTCSeconds(),
  };
}

export function occurrenceAtIndex(settings, index) {
  if (!settings || !Number.isInteger(index) || index < 0) return null;
  if (settings.interval.unit === "hours") {
    return new Date(
      settings.firstSendAt.getTime() + index * settings.interval.value * 60 * 60 * 1000
    );
  }
  return localPartsToInstant(
    addLocalDays(zonedParts(settings.firstSendAt, settings.timeZone), index * settings.interval.value),
    settings.timeZone
  );
}

function localDayOrdinal(date, timeZone) {
  const parts = zonedParts(date, timeZone);
  return Math.floor(Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000);
}

function mostRecentOccurrence(settings, now) {
  if (!settings || settings.firstSendAt.getTime() > now.getTime()) return null;
  let index = settings.interval.unit === "hours"
    ? Math.floor(
      (now.getTime() - settings.firstSendAt.getTime()) /
      (settings.interval.value * 60 * 60 * 1000)
    )
    : Math.max(0, Math.floor(
      (localDayOrdinal(now, settings.timeZone) - localDayOrdinal(settings.firstSendAt, settings.timeZone)) /
      settings.interval.value
    ));
  let dueAt = occurrenceAtIndex(settings, index);
  while (index > 0 && (!dueAt || dueAt.getTime() > now.getTime())) {
    index -= 1;
    dueAt = occurrenceAtIndex(settings, index);
  }
  let next = occurrenceAtIndex(settings, index + 1);
  while (next && next.getTime() <= now.getTime()) {
    index += 1;
    dueAt = next;
    next = occurrenceAtIndex(settings, index + 1);
  }
  return dueAt ? { index, dueAt } : null;
}

export function selectDueAutoNagOccurrence({ config, now = new Date(), lateWindowMinutes = 10 } = {}) {
  const normalized = normalizeAutoNagSettings(config);
  const current = resolveDate(now);
  if (!normalized.valid || !normalized.settings?.enabled || !current) return null;
  const occurrence = mostRecentOccurrence(normalized.settings, current);
  if (!occurrence) return null;
  const lowerBound = current.getTime() - Number(lateWindowMinutes) * 60 * 1000;
  return occurrence.dueAt.getTime() > lowerBound && occurrence.dueAt.getTime() <= current.getTime()
    ? occurrence
    : null;
}

export function selectNextAutoNagAt({ config, now = new Date() } = {}) {
  const normalized = normalizeAutoNagSettings(config);
  const current = resolveDate(now);
  if (!normalized.valid || !normalized.settings || !current) return null;
  if (normalized.settings.firstSendAt.getTime() > current.getTime()) {
    return new Date(normalized.settings.firstSendAt);
  }
  const recent = mostRecentOccurrence(normalized.settings, current);
  return recent ? occurrenceAtIndex(normalized.settings, recent.index + 1) : null;
}

export function validateAutoNagAuthoring({
  config,
  now = new Date(),
  votingDeadlineAt = null,
  earliestSlotAt = null,
} = {}) {
  const normalized = normalizeAutoNagSettings(config);
  if (!normalized.valid || !normalized.settings) {
    return { valid: false, error: "Auto-nag settings are invalid.", nextSendAt: null };
  }
  if (!normalized.settings.enabled) {
    return { valid: true, error: null, nextSendAt: null };
  }
  const nextSendAt = selectNextAutoNagAt({ config: normalized.settings, now });
  if (!nextSendAt) {
    return { valid: false, error: "Choose a valid auto-nag schedule.", nextSendAt: null };
  }
  const cutoff = resolveAutoNagCutoff({ votingDeadlineAt, earliestSlotAt });
  if (cutoff && nextSendAt.getTime() >= cutoff.getTime()) {
    return {
      valid: false,
      error: "Schedule the next auto-nag before voting closes or the first proposed session time.",
      nextSendAt,
    };
  }
  return { valid: true, error: null, nextSendAt };
}

export function resolveLatestNagDate(nagState, legacyNudgeLastSentAt) {
  return [nagState?.lastSentAt, legacyNudgeLastSentAt]
    .map(resolveDate)
    .filter(Boolean)
    .sort((left, right) => right.getTime() - left.getTime())[0] || null;
}

export function resolveAutoNagCooldown({ now = new Date(), nagState, legacyNudgeLastSentAt } = {}) {
  const current = resolveDate(now);
  const latest = resolveLatestNagDate(nagState, legacyNudgeLastSentAt);
  if (!current || !latest) return { onCooldown: false, remainingMs: 0, lastSentAt: latest };
  const remainingMs = Math.max(0, AUTO_NAG_COOLDOWN_MS - (current.getTime() - latest.getTime()));
  return { onCooldown: remainingMs > 0, remainingMs, lastSentAt: latest };
}

export function resolveAutoNagCutoff({ votingDeadlineAt, earliestSlotAt } = {}) {
  return [votingDeadlineAt, earliestSlotAt]
    .map(resolveDate)
    .filter(Boolean)
    .sort((left, right) => left.getTime() - right.getTime())[0] || null;
}

export function isAutoNagPastCutoff({ now = new Date(), votingDeadlineAt, earliestSlotAt } = {}) {
  const current = resolveDate(now);
  const cutoff = resolveAutoNagCutoff({ votingDeadlineAt, earliestSlotAt });
  return Boolean(current && cutoff && current.getTime() >= cutoff.getTime());
}
